import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "./api";
import {
  billingChanged,
  billingSnapshot,
  classifyCheckoutReturn,
  clearBillingSnapshot,
  readBillingSnapshot,
} from "./checkoutReturn";

/** `success` — the backend has applied the payment. `processing` — payment went
 *  through but the provider's webhook hadn't landed before we gave up waiting.
 *  `cancelled` — the user backed out on the provider's page (not an error). */
export type CheckoutOutcome = "success" | "processing" | "cancelled" | null;

const POLL_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Handles coming back from the payment provider — via the `?checkout=` return
 * URLs, and via the browser's Back button (bfcache restore or a fresh load
 * with the pre-payment snapshot still in storage, both meaning the checkout
 * was abandoned).
 *
 * On success the provider redirects the user back before (or racing with) its
 * webhook, so a single refetch often still shows the old plan. We poll
 * /billing/subscription against the snapshot taken before the redirect until
 * something changes, then hand back an outcome for the page to announce. With
 * no snapshot to compare against, success is never announced — the data is
 * refreshed quietly. The query parameter is dropped straight away so a reload
 * doesn't replay the message.
 *
 * `invoicesKey` changes once the payment settles — remount the invoice list with
 * it to pull in the new invoice.
 */
export function useCheckoutReturn(refreshSubscription: () => Promise<void>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [outcome, setOutcome] = useState<CheckoutOutcome>(null);
  // A quiet landing (`settle`) still needs the parameter stripped, without an
  // outcome to announce — tracked separately from `outcome` for that reason.
  const [settled, setSettled] = useState(false);
  const [invoicesKey, setInvoicesKey] = useState(0);
  // Survives the re-render caused by stripping the parameter, so the poll is
  // never started twice.
  const handled = useRef(false);
  const param = searchParams.get("checkout");

  useEffect(() => {
    if (handled.current) return;
    // Peeked, not consumed: if this run is torn down (React double-mounts
    // effects in development) the next one still has a baseline.
    const before = readBillingSnapshot();
    const action = classifyCheckoutReturn(param, before);
    if (action === "none") return;
    handled.current = true;
    let disposed = false;

    void (async () => {
      if (action === "cancelled") {
        // Either the provider's cancel URL, or a leftover snapshot with no
        // return parameter — a checkout abandoned via the Back button.
        setOutcome("cancelled");
        clearBillingSnapshot();
        return;
      }
      if (action === "settle") {
        // Success claimed but nothing to verify it against (manual visit to
        // the return URL, or storage was unavailable): refresh quietly, never
        // announce a payment we cannot confirm.
        await refreshSubscription().catch(() => {});
        if (disposed) return;
        clearBillingSnapshot();
        setSettled(true);
        setInvoicesKey((k) => k + 1);
        return;
      }

      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
        try {
          const data = await api.subscription();
          if (disposed) return;
          if (billingChanged(before, billingSnapshot(data))) {
            await refreshSubscription().catch(() => {});
            if (disposed) return;
            clearBillingSnapshot();
            setOutcome("success");
            setInvoicesKey((k) => k + 1);
            return;
          }
        } catch {
          /* transient gateway error — keep polling */
        }
        await sleep(POLL_INTERVAL_MS);
        if (disposed) return;
      }

      // Webhook still hasn't landed: show the paid-but-pending wording rather
      // than claiming failure, and still refresh what we can.
      await refreshSubscription().catch(() => {});
      if (disposed) return;
      clearBillingSnapshot();
      setOutcome("processing");
      setInvoicesKey((k) => k + 1);
    })();

    return () => {
      disposed = true;
      // Let a remount pick the work back up — otherwise the guard above would
      // block it and the aborted run would be the only one there ever was.
      handled.current = false;
    };
  }, [param, refreshSubscription]);

  // Coming back from the provider via the browser's Back button restores this
  // page from the bfcache — full JS state included, so an in-flight
  // "Redirecting…" survives and no effect re-runs. A snapshot still in storage
  // at that point means the checkout was abandoned: surface it as a cancel.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted || !readBillingSnapshot()) return;
      clearBillingSnapshot();
      handled.current = false;
      setOutcome("cancelled");
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Strip the parameter only once the outcome is known. Doing it inside the
  // effect above would change `param`, re-run that effect, and its cleanup
  // would abort the poll it had just started.
  useEffect(() => {
    if ((outcome == null && !settled) || !param) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("checkout");
        return next;
      },
      { replace: true },
    );
  }, [outcome, settled, param, setSearchParams]);

  return { outcome, invoicesKey };
}
